import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

// The HTML attribute that we add to html tags to test
const UNIQUE_TESTS_IDENTIFIER = 'data-id';
// The tags where the unique test identifier should be added
const TAGS_TO_TEST = ['input', 'select', 'button', 'a'];
// The separator to use if the unique identifier attribute has spaces
const UNIQUE_TESTS_IDENTIFIER_SEPARATOR = '_';



/**
 * Normalize a string by removing accents, converting it to lowercase,
 * replacing spaces with dashes, and removing dots.
 *
 * @param {string} str The string to normalize.
 * @returns {string} The normalized string.
 */
function normalize(str) {
  const TEXT_SPACE = ' ';
  const TEXT_EMPTY = '';

  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]|[\s+]|[\.-]/g, (match) => {
      if (match === TEXT_SPACE) return UNIQUE_TESTS_IDENTIFIER_SEPARATOR;
      else return TEXT_EMPTY;
    })
    .toLowerCase()
    .trim();
}

/**
 * Recursively get text content from the closest parent
 * @param {HTMLElement} element - The current HTML element
 * @returns {string|null} The text content of the closest parent, or null if none found
 */
function getTextFromClosestParent(element) {
  try {
    if (element.parentElement) {
      const text = element.parentElement.textContent.trim();
      if (text) {
        return text;
      }
      return getTextFromClosestParent(element.parentElement);

    }
  } catch (error) {
    console.error(element, error);
    return null;
  }
  return null;
}


/**
 * Generates an identifier string for a given HTML element. 
 * The identifier is based on either the element's label text, button text, 
 * placeholder attribute, name attribute, or text content, whichever is found first.
 * This identifier is normalized (accents removed, converted to lowercase, spaces replaced with dashes, and dots removed).
 *
 * @param {HTMLElement} element The element to generate an identifier for.
 * @returns {string} The generated identifier.
 */
function generateId(element) {
  // For Material-UI TextFields and Selects
  const label = document.querySelector(`label[for="${element.id}"]`);
  if (label) {
    return normalize(label.textContent);
  }

  // For Material-UI Buttons
  const buttonText = element.querySelector('span');
  if (buttonText) {
    return normalize(buttonText.textContent);
  }
  // If we cannot find the unique element id, we try to find placeholder, name or textContent
  // Or just a blank texy (this last case should never happen but BTW)
  let generatedId = element.getAttribute('placeholder') ||
    element.getAttribute('name') ||
    element.textContent;
  if (generatedId && generatedId.trim() != '') {
    return normalize(generatedId);
  }
  // Check ancestors for descriptive text
  const existentTextFromParent = getTextFromClosestParent(element);
  return normalize(existentTextFromParent || '')
}

/**
 * Add a "data-id" attribute to all elements of a certain type on the page.
 *
 * The "data-id" attribute's value is based on the element's text, placeholder or label,
 * depending on the element type. It is normalized for consistency.
 *
 * @param {string} tagName The type of element to add the attribute to (e.g., 'input', 'button').
 */
function addDataIdAttribute(tagName) {
  try {
    const elements = document.querySelectorAll(tagName);
    elements.forEach((element) => {
      const dataId = generateId(element);
      if (dataId) {
        element.setAttribute(UNIQUE_TESTS_IDENTIFIER, dataId);
      }
    });
  } catch (error) {
    console.error(tagName, error);
  }
}

/**
 * Add a "data-id" attribute to all interactive elements on the page.
 *
 * This is a convenience function that calls `addDataIdAttribute` for all relevant element types.
 */
function addDataIdAttributesForAllTags() {
  TAGS_TO_TEST.forEach(addDataIdAttribute);
}


function DataIdAttributeAdder() {
  const history = useHistory();

  useEffect(() => {
    // Add the data-id attribute immediately after the initial rendering.
    addDataIdAttributesForAllTags()

    // Add the data-id attribute every time the route changes.
    const unlisten = history.listen(() => {
      addDataIdAttributesForAllTags()
    });

    // Clean up when the component is unmounted.
    return unlisten;
  }, [history]);

  return null;  // This component does not need to render anything.
}

// Expose the addDataIdAttribute function through the window object to allow calls from automation.
window.addDataIdAttributesForAllTags = addDataIdAttributesForAllTags;

export default DataIdAttributeAdder;